import { status as AppointmentStatus } from '@spike/appointment-model';
import { CheckoutEnrollment } from '@spike/membership-model';
import { OrderItem, OrderItems } from '@spike/order-model';
import Pet from '@spike/pet-model';
import clsx from 'clsx';
import { appointmentHasBoardingDeal } from 'components/Checkout2/utils/boardingDealMembership';
import PetCard from 'components/UI/PetCard';
import { FunctionComponent, useMemo } from 'react';
import ExpandableSection from '../ExpandableSection';
import OrderItemMemberships from './OrderItemMemebrships';
import styles from './PetMemberships.module.css';

export interface PetMembershipsProps {
    pet: Pet;
    petOrderItems: OrderItems;
    enrollmentsByOrderItem: Map<number, Array<CheckoutEnrollment>>;
    saving: boolean;
    loading: boolean;
    className?: string;
    onSave?: (orderItemId: number, enrollmentId: number) => void;
    onDelete?: (orderItemId: number) => void;
}

export const PetMemberships: FunctionComponent<PetMembershipsProps> = props => {
    const header = <PetCard pet={props.pet} />;

    const orderItemsWithEnrollments = useMemo(() => {
        const appointmentOrderItems = props.petOrderItems.appointments
            .filter(
                item =>
                    ![AppointmentStatus.CANCELLED, AppointmentStatus.DECLINED, AppointmentStatus.NO_SHOW].includes(
                        item.appointment.status.id
                    )
            )
            .map(item => ({
                orderItem: item.orderItem,
                membershipBlockedByBoardingDeal: appointmentHasBoardingDeal(item.appointment)
            }));

        const otherOrderItems: Array<{
            orderItem: OrderItem;
            membershipBlockedByBoardingDeal: boolean;
        }> = [
            ...props.petOrderItems.addOnServices.map(item => ({
                orderItem: item.orderItem,
                membershipBlockedByBoardingDeal: false
            })),
            ...props.petOrderItems.products.map(item => ({
                orderItem: item.orderItem,
                membershipBlockedByBoardingDeal: false
            }))
        ];

        return [...appointmentOrderItems, ...otherOrderItems].filter(
            entry =>
                props.enrollmentsByOrderItem.get(entry.orderItem.id!) !== undefined ||
                entry.orderItem.redeem !== undefined
        );
    }, [props.petOrderItems, props.enrollmentsByOrderItem]);

    return orderItemsWithEnrollments.length === 0 ? null : (
        <ExpandableSection
            header={header}
            initExpanded={true}
            bottomPadding={0}
            className={clsx(styles.container, props.className)}
        >
            {orderItemsWithEnrollments.map(({ orderItem, membershipBlockedByBoardingDeal }) => (
                <OrderItemMemberships
                    key={orderItem.id}
                    orderItem={orderItem}
                    checkoutEnrollments={props.enrollmentsByOrderItem.get(orderItem.id ?? 0) ?? []}
                    saving={props.saving}
                    membershipBlockedByBoardingDeal={membershipBlockedByBoardingDeal}
                    onSelect={enrollmentId => props.onSave && orderItem.id && props.onSave(orderItem.id, enrollmentId)}
                    onDelete={() => props.onDelete && orderItem.id && props.onDelete(orderItem.id)}
                />
            ))}
        </ExpandableSection>
    );
};

export default PetMemberships;
